/**
 * Utility functions for CollabCanvas
 */

// Color palette for user cursors and presence indicators
// Carefully selected for good contrast on white background and visual distinction
export const COLOR_PALETTE = [
  '#FF5733', // Red-Orange
  '#33C1FF', // Light Blue  
  '#8E44AD', // Purple
  '#F39C12', // Orange
  '#27AE60', // Green
  '#E74C3C', // Red
  '#3498DB', // Blue
  '#9B59B6', // Purple-Pink
  '#E67E22', // Orange-Red
  '#1ABC9C'  // Teal
];

/**
 * Generate a consistent color for a user based on their userId
 * Uses a simple hash function to ensure same userId always gets same color
 * @param {string} userId - Firebase user ID
 * @returns {string} Hex color code from COLOR_PALETTE
 */
export function generateUserColor(userId) {
  if (!userId || typeof userId !== 'string') {
    // Fallback color for invalid userId
    return COLOR_PALETTE[0];
  }

  // Simple hash function for consistent color assignment
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    const char = userId.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }

  // Ensure positive index within COLOR_PALETTE bounds
  const colorIndex = Math.abs(hash) % COLOR_PALETTE.length;
  return COLOR_PALETTE[colorIndex];
}

/**
 * Generate user display name with fallbacks
 * Priority: displayName > email prefix > "Anonymous User"
 * @param {Object} user - Firebase user object
 * @returns {string} Display name for UI
 */
export function generateDisplayName(user) {
  if (!user) return 'Anonymous User';
  
  // Use displayName if available
  if (user.displayName && user.displayName.trim()) {
    return user.displayName.trim();
  }
  
  // Extract name from email
  if (user.email) {
    const emailPrefix = user.email.split('@')[0];
    // Capitalize first letter and replace dots/underscores with spaces
    const cleanName = emailPrefix
      .replace(/[._]/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
    return cleanName;
  }
  
  return 'Anonymous User';
}

/**
 * Create user presence data object
 * @param {Object} user - Firebase user object
 * @param {number} x - Initial cursor X position
 * @param {number} y - Initial cursor Y position
 * @returns {Object} User presence data for Realtime Database
 */
export function createUserPresenceData(user, x = 0, y = 0) {
  const color = generateUserColor(user.uid);
  const displayName = generateDisplayName(user);
  
  return {
    userId: user.uid,
    displayName,
    cursorColor: color,
    cursorX: x,
    cursorY: y,
    lastSeen: Date.now(),
    isOnline: true,
    joinedAt: Date.now()
  };
}

/**
 * Truncate text to specified length with ellipsis
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length (default: 20)
 * @returns {string} Truncated text
 */
export function truncateText(text, maxLength = 20) {
  if (!text || typeof text !== 'string') return '';
  
  if (text.length <= maxLength) return text;
  
  return text.substring(0, maxLength - 3) + '...';
}

/**
 * Debounce function to limit rapid function calls
 * @param {Function} func - Function to debounce
 * @param {number} delay - Delay in milliseconds
 * @returns {Function} Debounced function
 */
export function debounce(func, delay) {
  let timeoutId;
  
  return function (...args) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(this, args), delay);
  };
}

/**
 * Throttle function to limit function execution frequency
 * @param {Function} func - Function to throttle
 * @param {number} limit - Time limit in milliseconds
 * @returns {Function} Throttled function
 */
export function throttle(func, limit) {
  let inThrottle;
  
  return function (...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}

/**
 * Calculate distance between two points
 * @param {number} x1 - First point X
 * @param {number} y1 - First point Y  
 * @param {number} x2 - Second point X
 * @param {number} y2 - Second point Y
 * @returns {number} Distance in pixels
 */
export function calculateDistance(x1, y1, x2, y2) {
  return Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
}

/**
 * Clamp a value between min and max bounds
 * @param {number} value - Value to clamp
 * @param {number} min - Minimum value
 * @param {number} max - Maximum value
 * @returns {number} Clamped value
 */
export function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

/**
 * Convert canvas coordinates to screen coordinates
 * @param {number} canvasX - Canvas X coordinate
 * @param {number} canvasY - Canvas Y coordinate
 * @param {Object} stageTransform - Stage transformation { x, y, scaleX, scaleY }
 * @returns {Object} Screen coordinates { x, y }
 */
export function canvasToScreen(canvasX, canvasY, stageTransform) {
  return {
    x: (canvasX * stageTransform.scaleX) + stageTransform.x,
    y: (canvasY * stageTransform.scaleY) + stageTransform.y
  };
}

/**
 * Convert screen coordinates to canvas coordinates
 * @param {number} screenX - Screen X coordinate
 * @param {number} screenY - Screen Y coordinate
 * @param {Object} stageTransform - Stage transformation { x, y, scaleX, scaleY }
 * @returns {Object} Canvas coordinates { x, y }
 */
export function screenToCanvas(screenX, screenY, stageTransform) {
  return {
    x: (screenX - stageTransform.x) / stageTransform.scaleX,
    y: (screenY - stageTransform.y) / stageTransform.scaleY
  };
}
